import { useState, useCallback } from "react";

interface UploadStepProps {
  onUploaded: () => void;
  onBack: () => void;
}

export default function UploadStep({ onUploaded, onBack }: UploadStepProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleFile = useCallback((f: File) => {
    setFile(f);
    setIsUploading(true);
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => onUploaded(), 400);
          return 100;
        }
        return prev + Math.floor(Math.random() * 12) + 4;
      });
    }, 180);
  }, [onUploaded]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  }, [handleFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
  };

  const formatSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-500 hover:text-white text-sm mb-8 transition-colors cursor-pointer"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-left-line" />
        </span>
        뒤로 가기
      </button>

      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          영상을 업로드하세요
        </h2>
        <p className="text-gray-400 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
          AI가 자동으로 하이라이트 구간을 감지하고 숏폼으로 최적화합니다
        </p>
      </div>

      {!file ? (
        <>
          {/* Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-2xl p-16 text-center transition-all duration-300 ${
              isDragging
                ? "border-[#00D9FF] bg-[#00D9FF]/8"
                : "border-white/15 bg-[#0F1419] hover:border-[#00D9FF]/40 hover:bg-[#0F1419]/80"
            }`}
          >
            {isDragging && (
              <div className="absolute inset-0 rounded-2xl bg-[#00D9FF]/5 animate-pulse" />
            )}
            <div className="relative z-10">
              <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-[#00D9FF]/10 border border-[#00D9FF]/20 mx-auto mb-6">
                <i className={`ri-upload-cloud-2-line text-4xl transition-colors duration-200 ${isDragging ? "text-[#00D9FF]" : "text-gray-500"}`} />
              </div>
              <p className="text-white font-semibold text-lg mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {isDragging ? "여기에 놓으세요" : "영상 파일을 드래그하거나 클릭하여 업로드"}
              </p>
              <p className="text-gray-500 text-sm mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                MP4, MOV 지원 &middot; 최대 500MB
              </p>
              <label className="inline-flex items-center gap-2 bg-[#00D9FF] hover:bg-[#00D9FF]/90 text-[#0A0E14] font-bold text-sm px-6 py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-folder-open-line" />
                </span>
                파일 선택
                <input
                  type="file"
                  accept=".mp4,.mov,video/mp4,video/quicktime"
                  className="hidden"
                  onChange={handleInputChange}
                />
              </label>
            </div>
          </div>

          {/* Format info */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { icon: "ri-film-line", label: "MP4 / MOV", desc: "지원 포맷" },
              { icon: "ri-time-line", label: "최대 3시간", desc: "영상 길이" },
              { icon: "ri-shield-check-line", label: "보안 처리", desc: "암호화 업로드" },
            ].map((item) => (
              <div key={item.label} className="bg-[#0F1419] border border-white/8 rounded-xl p-4 text-center">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${item.icon} text-[#00D9FF] text-lg`} />
                </div>
                <div className="text-white text-xs font-semibold mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{item.label}</div>
                <div className="text-gray-500 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        /* Upload Progress */
        <div className="bg-[#0F1419] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#00D9FF]/10 shrink-0">
              <i className="ri-file-video-line text-2xl text-[#00D9FF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {file.name}
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {formatSize(file.size)}
              </p>
            </div>
            {uploadProgress === 100 ? (
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#10B981]/20">
                <i className="ri-check-line text-[#10B981]" />
              </div>
            ) : (
              <span className="text-[#00D9FF] font-bold text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {uploadProgress}%
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden mb-3">
            <div
              className="h-full rounded-full transition-all duration-200"
              style={{
                width: `${uploadProgress}%`,
                background: uploadProgress === 100 ? "#10B981" : "linear-gradient(90deg, #00D9FF, #10B981)",
              }}
            />
          </div>

          <p className="text-gray-500 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
            {uploadProgress < 100
              ? `업로드 중... 예상 처리 시간: 약 90초`
              : "업로드 완료! AI 분석을 시작합니다..."}
          </p>
        </div>
      )}
    </div>
  );
}
